import React, { createContext } from 'react';
import { useTranslation } from 'react-i18next';
import DraftConflictModal from '@components/atoms/modals/draft-conflict-modal.tsx';
import { openCloudVersionInNewTab } from '@utils/localStorage/draft-conflict.ts';
import type {
  SharedCaptionContextType,
  SharedCaptionProviderProps
} from './shared-caption-context-state';
import { useSharedCaptionContextLogic } from './use-shared-caption-context-logic';

const SharedCaptionContext = createContext<
  SharedCaptionContextType | undefined
>(undefined);

export function SharedCaptionProvider({
  children,
  session,
  document,
  token
}: SharedCaptionProviderProps) {
  const { t } = useTranslation();
  const {
    contextValue,
    conflict,
    resolveConflictKeepLocal,
    resolveConflictLoadCloud
  } = useSharedCaptionContextLogic({ session, document, token });

  return (
    <SharedCaptionContext.Provider value={contextValue}>
      {conflict ? (
        <DraftConflictModal
          isOpen
          localSavedAt={conflict.localTimestamp}
          serverSavedAt={conflict.serverTimestamp}
          localCount={conflict.localCount}
          serverCount={conflict.serverCount}
          unit={t('caption-cues-unit', 'cues')}
          onKeepLocal={resolveConflictKeepLocal}
          onLoadCloud={resolveConflictLoadCloud}
          onViewCloud={openCloudVersionInNewTab}
        />
      ) : (
        children
      )}
    </SharedCaptionContext.Provider>
  );
}

export function useSharedCaption() {
  const context = React.useContext(SharedCaptionContext);
  if (context === undefined) {
    throw new Error('useCaption must be used within a CaptionProvider');
  }
  return context;
}
